import { z } from "zod";
import {
  snapshotMutation,
  type PhotoAttachTarget,
  type SnapshotMutation,
  type SnapshotMutationKind,
} from "../sync/snapshot-mutation.js";
import {
  decodeBreakerInstallation,
  encodeBreakerInstallation,
} from "./circuit.js";
import { decodeDevice, deviceJsonSchema, encodeDevice } from "./device.js";
import { CodecError, instantSchema } from "./envelope.js";
import {
  encodeLogicalTimestamp,
  logicalTimestampJsonSchema,
} from "./logical-timestamp.js";
import { decodePanel, encodePanel, panelJsonSchema } from "./panel.js";
import { decodeRoom, encodeRoom, roomJsonSchema } from "./room.js";
import { decodeSite, encodeSite, siteJsonSchema } from "./site.js";

/**
 * Phase 9f3 codec for the `SnapshotMutation` family. Sits alongside
 * `codec/operation-log.ts`'s per-field `Operation` codec; the
 * top-level `OperationLog` codec composes both. Wire shape mirrors
 * the `Operation` framing — `mutationId`, `timestamp`, `issuedAt`,
 * `kind`, then per-kind payload fields. Discriminant is the top-
 * level `kind` (mutations carry no `fieldPath`); the per-kind
 * payload schema is dispatched in encode/decode just as Phase 9a
 * dispatches on `fieldPath.kind`.
 *
 * Determinism: declared key order on every emitted object, canonical
 * millisecond-precision ISO 8601 for `issuedAt`, declared key order
 * within the per-kind payload, arrays preserved in source order. The
 * codec validates structural invariants only — non-empty ids,
 * canonical instants, payload shape under the per-kind schema.
 * Cross-aggregate invariants (panel-create's building id exists in
 * the site, link-sub-panel's parent circuit exists in the graph) are
 * re-checked at apply time.
 *
 * Decode runs the input through the per-kind schema, then routes
 * through `snapshotMutation()` so the framing invariants (non-empty
 * mutationId, canonical instant, per-arm id non-emptiness, no
 * duplicate device ids in a `room-device-scan`) fire on read just as
 * they do on construction.
 */

const photoAttachTargetJsonSchema: z.ZodType<PhotoAttachTarget> =
  z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("site"), siteId: z.string().min(1) }),
    z.object({ kind: z.literal("device"), deviceId: z.string().min(1) }),
    z.object({ kind: z.literal("circuit"), circuitId: z.string().min(1) }),
  ]);

function encodePhotoAttachTarget(
  t: PhotoAttachTarget,
): Record<string, unknown> {
  switch (t.kind) {
    case "site":
      return { kind: "site", siteId: t.siteId };
    case "device":
      return { kind: "device", deviceId: t.deviceId };
    case "circuit":
      return { kind: "circuit", circuitId: t.circuitId };
  }
}

const snapshotMutationFrameSchema = z.object({
  mutationId: z.string().min(1),
  timestamp: logicalTimestampJsonSchema,
  issuedAt: instantSchema,
});

/**
 * Per-kind discriminated union over the *payload* shape (every arm
 * adds its own non-frame fields beneath the same frame fields). The
 * full mutation schema is the frame intersected with the per-kind
 * payload — same composition Phase 9a uses for `Operation`.
 */
const snapshotMutationPayloadSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("site-create"), site: siteJsonSchema }),
  z.object({ kind: z.literal("site-delete"), siteId: z.string().min(1) }),
  z.object({ kind: z.literal("room-create"), room: roomJsonSchema }),
  z.object({ kind: z.literal("room-delete"), roomId: z.string().min(1) }),
  z.object({ kind: z.literal("device-create"), device: deviceJsonSchema }),
  z.object({ kind: z.literal("device-delete"), deviceId: z.string().min(1) }),
  z.object({
    kind: z.literal("panel-create"),
    panel: panelJsonSchema,
    buildingId: z.string().min(1),
  }),
  z.object({ kind: z.literal("panel-delete"), panelId: z.string().min(1) }),
  z.object({ kind: z.literal("panel-scan"), panel: panelJsonSchema }),
  z.object({
    kind: z.literal("room-device-scan"),
    devices: z.array(deviceJsonSchema),
  }),
  z.object({
    kind: z.literal("link-sub-panel"),
    subPanelId: z.string().min(1),
    parentCircuitId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("unlink-sub-panel"),
    subPanelId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("photo-attach"),
    target: photoAttachTargetJsonSchema,
    photoId: z.string().min(1),
  }),
]);

export const snapshotMutationJsonSchema = snapshotMutationFrameSchema.and(
  snapshotMutationPayloadSchema,
);

export type SnapshotMutationJson = {
  readonly mutationId: string;
  readonly timestamp: { readonly logical: number; readonly deviceId: string };
  readonly issuedAt: string;
  readonly kind: SnapshotMutationKind;
} & Record<string, unknown>;

export function encodeSnapshotMutation(
  mut: SnapshotMutation,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    mutationId: mut.mutationId,
    timestamp: encodeLogicalTimestamp(mut.timestamp),
    issuedAt: mut.issuedAt,
    kind: mut.kind,
  };
  switch (mut.kind) {
    case "site-create":
      out["site"] = encodeSite(mut.site);
      return out;
    case "site-delete":
      out["siteId"] = mut.siteId;
      return out;
    case "room-create":
      out["room"] = encodeRoom(mut.room);
      return out;
    case "room-delete":
      out["roomId"] = mut.roomId;
      return out;
    case "device-create":
      out["device"] = encodeDevice(mut.device);
      return out;
    case "device-delete":
      out["deviceId"] = mut.deviceId;
      return out;
    case "panel-create":
      out["panel"] = encodePanel(mut.panel, encodeBreakerInstallation);
      out["buildingId"] = mut.buildingId;
      return out;
    case "panel-delete":
      out["panelId"] = mut.panelId;
      return out;
    case "panel-scan":
      out["panel"] = encodePanel(mut.panel, encodeBreakerInstallation);
      return out;
    case "room-device-scan":
      out["devices"] = mut.devices.map(encodeDevice);
      return out;
    case "link-sub-panel":
      out["subPanelId"] = mut.subPanelId;
      out["parentCircuitId"] = mut.parentCircuitId;
      return out;
    case "unlink-sub-panel":
      out["subPanelId"] = mut.subPanelId;
      return out;
    case "photo-attach":
      out["target"] = encodePhotoAttachTarget(mut.target);
      out["photoId"] = mut.photoId;
      return out;
  }
}

export function decodeSnapshotMutation(input: unknown): SnapshotMutation {
  const frame = snapshotMutationFrameSchema.safeParse(input);
  if (!frame.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeSnapshotMutation: ${frame.error.message}`,
    );
  }
  const payload = snapshotMutationPayloadSchema.safeParse(input);
  if (!payload.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeSnapshotMutation: ${payload.error.message}`,
    );
  }
  const f = frame.data;
  const p = payload.data;
  const built = buildMutation(f, p);
  return snapshotMutation(built);
}

function buildMutation(
  frame: z.infer<typeof snapshotMutationFrameSchema>,
  payload: z.infer<typeof snapshotMutationPayloadSchema>,
): SnapshotMutation {
  const head = {
    mutationId: frame.mutationId,
    timestamp: frame.timestamp,
    issuedAt: frame.issuedAt,
  };
  switch (payload.kind) {
    case "site-create":
      return { ...head, kind: "site-create", site: decodeSite(payload.site) };
    case "site-delete":
      return { ...head, kind: "site-delete", siteId: payload.siteId };
    case "room-create":
      return { ...head, kind: "room-create", room: decodeRoom(payload.room) };
    case "room-delete":
      return { ...head, kind: "room-delete", roomId: payload.roomId };
    case "device-create":
      return {
        ...head,
        kind: "device-create",
        device: decodeDevice(payload.device),
      };
    case "device-delete":
      return { ...head, kind: "device-delete", deviceId: payload.deviceId };
    case "panel-create":
      return {
        ...head,
        kind: "panel-create",
        panel: decodePanel(payload.panel, decodeBreakerInstallation),
        buildingId: payload.buildingId,
      };
    case "panel-delete":
      return { ...head, kind: "panel-delete", panelId: payload.panelId };
    case "panel-scan":
      return {
        ...head,
        kind: "panel-scan",
        panel: decodePanel(payload.panel, decodeBreakerInstallation),
      };
    case "room-device-scan":
      return {
        ...head,
        kind: "room-device-scan",
        devices: payload.devices.map(decodeDevice),
      };
    case "link-sub-panel":
      return {
        ...head,
        kind: "link-sub-panel",
        subPanelId: payload.subPanelId,
        parentCircuitId: payload.parentCircuitId,
      };
    case "unlink-sub-panel":
      return {
        ...head,
        kind: "unlink-sub-panel",
        subPanelId: payload.subPanelId,
      };
    case "photo-attach":
      return {
        ...head,
        kind: "photo-attach",
        target: payload.target,
        photoId: payload.photoId,
      };
  }
}
